import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useModal } from 'hooks/useModal';
import { useCurrentWidth } from 'effects';
import { IMediaGalleryGridProps } from './types';
import { IMediaImageUpload } from '../MediaGallery/types';
import { MediaCarouselModal } from '../MediaGallery/MediaCarouselModal';

interface IGridImageProps {
  upload: IMediaImageUpload;
  index: number;
  onClick: (uploadIndex: number) => void;
  overlayText?: string;
  baseUrlPath: string;
}

const GridImage = (props: IGridImageProps) => {
  // Create srcset string for responsive images
  const srcset = Object.entries(props.upload.sizes)
    .map(([size, details]) => `${props.baseUrlPath}${details.path} ${details.width}w`)
    .join(', ');

  // Use the small size as the default src
  const defaultSrc = props.upload.isVimeoThumbnail
    ? props.upload.sizes.s.path
    : `${props.baseUrlPath}${props.upload.sizes.s.path}`;

  const sizes = [
    `(max-width: 640px) ${props.upload.sizes.s.width}px`,
    `(max-width: 768px) ${props.upload.sizes.m.width}px`,
    `(max-width: 1024px) ${props.upload.sizes.l.width}px`,
    `(max-width: 1279px) ${props.upload.sizes.xl.width}px`,
  ].join(', ');

  return (
    <div className="flex flex-col gap-[5px]">
      <div
        onClick={() => props.onClick(props.index)}
        className="relative bg-cover bg-center cursor-pointer w-full aspect-square"
      >
        <img
          style={{
            backgroundImage: `url('${defaultSrc}')`,
          }}
          src={defaultSrc}
          srcSet={props.upload.isVimeoThumbnail ? undefined : srcset}
          sizes={props.upload.isVimeoThumbnail ? undefined : sizes}
          alt={props.upload.displayName}
          className="object-cover w-full h-full bg-cover bg-center rounded-[3px]"
          loading="lazy"
        />

        {props.overlayText && (
          <span className="bg-black/50 w-full h-full absolute top-0 left-0 right-0 bottom-0 rounded-[3px]">
            <span className="text-white font-hurmegeometric-sans text-14px underline absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
              {props.overlayText}
            </span>
          </span>
        )}
      </div>

      {/* Display name below image */}
      <span className="font-pt-sans text-[15px] leading-[19px] text-black text-left">{props.upload.displayName}</span>
    </div>
  );
};

export const MediaGalleryGrid = (props: IMediaGalleryGridProps) => {
  const { uploads, titleForModal, baseUrlPath, enableBulkDownload } = props;
  const { isSmOrLarger, isMdOrLarger, isLgOrLarger } = useCurrentWidth();
  const modalOps = useModal<{
    index: number;
  }>();

  // Filter to only image uploads
  const imageUploads = uploads.filter(u => u.fileType === 'image');

  // Determine number of columns based on breakpoint
  const columns = useMemo(() => {
    if (isMdOrLarger) return 6; // Desktop: 6 columns
    if (isMdOrLarger) return 4; // Tablet: 4 columns
    return 3; // Mobile: 3 columns
  }, [isLgOrLarger, isMdOrLarger]);

  // Calculate max images to show (3 rows)
  const maxVisibleImages = columns * 3;

  // Determine visible images
  const visibleImages = imageUploads.slice(0, maxVisibleImages);
  const remainingCount = imageUploads.length - maxVisibleImages;

  const handleOnClickImage = useCallback(
    async (uploadIndex: number) => {
      await modalOps.showModal({
        index: uploadIndex,
      });
    },
    [modalOps]
  );

  // Determine grid columns class
  const gridColsClass = isLgOrLarger ? 'grid-cols-6' : isMdOrLarger ? 'grid-cols-4' : 'grid-cols-3';

  return (
    <>
      <div className={classNames('grid gap-[20px]', gridColsClass)}>
        {visibleImages.map((upload, index) => {
          const isLastImage = index === visibleImages.length - 1;
          const showOverlay = isLastImage && remainingCount > 0;

          return (
            <GridImage
              key={index}
              index={index}
              onClick={handleOnClickImage}
              upload={upload}
              baseUrlPath={baseUrlPath}
              overlayText={showOverlay ? `+${remainingCount} photos` : undefined}
            />
          );
        })}
      </div>

      <MediaCarouselModal
        isOpen={modalOps.isOpen}
        onClose={modalOps.handleClose}
        titleForModal={titleForModal}
        images={imageUploads.map(u => {
          return {
            displayName: u.displayName,
            url: u.isVimeoThumbnail ? u.sizes.original.path : `${baseUrlPath}${u.sizes.original.path}`,
            uuid: u.uuid,
          };
        })}
        initialSlide={modalOps.modalData?.index || 0}
        enableBulkDownload={enableBulkDownload}
      />
    </>
  );
};
